import React, { forwardRef } from 'react';
import { Box, Typography, Chip, TextField, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import RemoveIcon from '@mui/icons-material/Remove';

export const modernTableStyle = {
  borderRadius: '16px',
  border: '1px solid #e2e8f0',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
  overflow: 'hidden',
  backgroundColor: '#ffffff',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

export const modernTableIcons = {
  Add: forwardRef((props, ref) => (
    <Box
      ref={ref}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.75,
        py: 0.8,
        borderRadius: '8px',
        backgroundColor: '#2563eb',
        color: '#ffffff',
        fontWeight: 600,
        fontSize: '0.8125rem',
        cursor: 'pointer',
        boxShadow: '0 2px 6px rgba(37, 99, 235, 0.25)',
        transition: 'all 0.15s ease',
        '&:hover': {
          backgroundColor: '#1d4ed8',
          boxShadow: '0 4px 10px rgba(37, 99, 235, 0.35)'
        }
      }}
    >
      <AddIcon sx={{ fontSize: 18 }} />
      <span>Novo Registro</span>
    </Box>
  )),
  Check: forwardRef((props, ref) => (
    <Box
      ref={ref}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.5,
        py: 0.65,
        borderRadius: '8px',
        backgroundColor: '#16a34a',
        color: '#ffffff !important',
        fontWeight: 700,
        fontSize: '0.75rem',
        boxShadow: '0 2px 4px rgba(22, 163, 74, 0.25)',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        '&:hover': {
          backgroundColor: '#15803d',
          boxShadow: '0 4px 6px rgba(22, 163, 74, 0.35)',
          transform: 'translateY(-1px)'
        }
      }}
    >
      <CheckIcon sx={{ fontSize: 16, color: '#ffffff' }} />
      <span style={{ color: '#ffffff' }}>Salvar</span>
    </Box>
  )),
  Clear: forwardRef((props, ref) => (
    <Box
      ref={ref}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.25,
        py: 0.65,
        borderRadius: '8px',
        backgroundColor: '#f8fafc',
        color: '#475569 !important',
        border: '1px solid #cbd5e1',
        fontWeight: 600,
        fontSize: '0.75rem',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        '&:hover': {
          backgroundColor: '#f1f5f9',
          color: '#0f172a !important'
        }
      }}
    >
      <CloseIcon sx={{ fontSize: 15, color: '#475569' }} />
      <span>Cancelar</span>
    </Box>
  )),
  Edit: forwardRef((props, ref) => (
    <Tooltip title="Editar registro">
      <Box
        ref={ref}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          borderRadius: '8px',
          backgroundColor: '#eff6ff',
          color: '#2563eb',
          border: '1px solid #dbeafe',
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: '#2563eb',
            color: '#ffffff',
            borderColor: '#2563eb'
          }
        }}
      >
        <EditIcon sx={{ fontSize: 17 }} />
      </Box>
    </Tooltip>
  )),
  Delete: forwardRef((props, ref) => (
    <Tooltip title="Excluir registro">
      <Box
        ref={ref}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          borderRadius: '8px',
          backgroundColor: '#fef2f2',
          color: '#ef4444',
          border: '1px solid #fee2e2',
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: '#ef4444',
            color: '#ffffff',
            borderColor: '#ef4444'
          }
        }}
      >
        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
      </Box>
    </Tooltip>
  )),
  Search: forwardRef((props, ref) => <SearchIcon ref={ref} sx={{ color: '#64748b', fontSize: 20 }} />),
  ResetSearch: forwardRef((props, ref) => <CloseIcon ref={ref} sx={{ color: '#94a3b8', fontSize: 18, cursor: 'pointer' }} />),
  Export: forwardRef((props, ref) => <SaveAltIcon ref={ref} sx={{ color: '#475569', fontSize: 20 }} />),
  FirstPage: forwardRef((props, ref) => <FirstPageIcon ref={ref} sx={{ color: '#475569' }} />),
  LastPage: forwardRef((props, ref) => <LastPageIcon ref={ref} sx={{ color: '#475569' }} />),
  NextPage: forwardRef((props, ref) => <ChevronRightIcon ref={ref} sx={{ color: '#475569' }} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeftIcon ref={ref} sx={{ color: '#475569' }} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownwardIcon ref={ref} sx={{ color: '#38bdf8', fontSize: 18 }} />),
  Filter: forwardRef((props, ref) => <FilterListIcon ref={ref} sx={{ color: '#475569' }} />),
  DetailPanel: forwardRef((props, ref) => <ChevronRightIcon ref={ref} sx={{ color: '#475569' }} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumnIcon ref={ref} sx={{ color: '#475569' }} />),
  ThirdStateCheck: forwardRef((props, ref) => <RemoveIcon ref={ref} sx={{ color: '#94a3b8' }} />),
};

export const modernTableLocalization = {
  body: {
    emptyDataSourceMessage: 'Nenhum registro encontrado.',
    addTooltip: 'Adicionar novo registro',
    deleteTooltip: 'Excluir registro',
    editTooltip: 'Editar linha',
    filterRowCheckTooltip: 'Filtrar',
    editRow: {
      deleteText: 'Tem certeza que deseja excluir este registro?',
      cancelTooltip: 'Cancelar edição',
      saveTooltip: 'Salvar alterações'
    }
  },
  toolbar: {
    searchTooltip: 'Pesquisar',
    searchPlaceholder: 'Buscar na tabela...',
    exportTitle: 'Exportar dados',
    exportName: 'Exportar como CSV',
    exportAriaLabel: 'Exportar',
    showColumnsTitle: 'Colunas visíveis',
    showColumnsAriaLabel: 'Mostrar ou ocultar colunas'
  },
  header: {
    actions: 'Ações'
  },
  pagination: {
    labelRowsSelect: 'linhas',
    labelDisplayedRows: '{from}-{to} de {count}',
    firstTooltip: 'Primeira página',
    previousTooltip: 'Página anterior',
    nextTooltip: 'Próxima página',
    lastTooltip: 'Última página'
  }
};

export const TableEditInput = ({ value, onChange, placeholder, fullWidth = true, autoFocus, type = 'text' }) => (
  <TextField
    size="small"
    type={type}
    fullWidth={fullWidth}
    autoFocus={autoFocus}
    value={value !== undefined && value !== null ? value : ''}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    variant="outlined"
    sx={{
      '& .MuiOutlinedInput-root': {
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        fontSize: '0.8125rem',
        fontWeight: 500,
        color: '#0f172a',
        transition: 'all 0.15s ease',
        '& fieldset': {
          borderColor: '#cbd5e1',
          borderWidth: '1.5px',
        },
        '&:hover fieldset': {
          borderColor: '#94a3b8',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#2563eb',
          borderWidth: '2px',
          boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.15)',
        },
      },
      '& .MuiOutlinedInput-input': {
        padding: '7px 10px',
      }
    }}
  />
);

export const TablePlateEditInput = ({ value, onChange }) => {
  const handleChange = (e) => {
    const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7);
    onChange(raw);
  };
  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column' }}>
      <TextField
        size="small"
        value={value || ''}
        onChange={handleChange}
        placeholder="ABC1D23"
        inputProps={{
          maxLength: 7,
          style: {
            textTransform: 'uppercase',
            fontWeight: 800,
            fontFamily: 'monospace',
            letterSpacing: '0.12em',
            textAlign: 'center',
            padding: '7px 8px',
            fontSize: '0.875rem'
          }
        }}
        sx={{
          width: 120,
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            border: '1.5px solid #003399',
            boxShadow: '0 1px 3px rgba(0, 51, 153, 0.15)',
            '& fieldset': { borderColor: 'transparent' },
            '&:hover fieldset': { borderColor: 'transparent' },
            '&.Mui-focused fieldset': { borderColor: 'transparent' },
          }
        }}
      />
    </Box>
  );
};

export const TableSelectEditInput = ({ value, onChange, options = [], placeholder = 'Selecione...' }) => (
  <TextField
    select
    size="small"
    fullWidth
    value={value !== undefined && value !== null ? value : ''}
    onChange={(e) => onChange(e.target.value)}
    SelectProps={{ native: true }}
    sx={{
      '& .MuiOutlinedInput-root': {
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        fontSize: '0.8125rem',
        fontWeight: 500,
        '& fieldset': { borderColor: '#cbd5e1', borderWidth: '1.5px' },
        '&:hover fieldset': { borderColor: '#94a3b8' },
        '&.Mui-focused fieldset': { borderColor: '#2563eb', borderWidth: '2px' },
      },
      '& .MuiOutlinedInput-input': {
        padding: '7px 10px',
      }
    }}
  >
    <option value="" disabled>{placeholder}</option>
    {options.map((opt) => (
      <option key={opt.value} value={opt.value}>
        {opt.label}
      </option>
    ))}
  </TextField>
);

export const modernTableOptions = (selectedRowId, additionalOptions = {}) => ({
  exportButton: true,
  exportAllData: true,
  addRowPosition: 'first',
  actionsColumnIndex: -1,
  searchFieldStyle: {
    borderRadius: '8px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    padding: '4px 12px',
    fontSize: '0.875rem',
    fontFamily: 'Inter, sans-serif'
  },
  rowStyle: rowData => ({
    backgroundColor: (selectedRowId === rowData?.tableData?.id) ? '#f0fdf4' : '#ffffff',
    borderBottom: '1px solid #f1f5f9',
    fontSize: '0.875rem',
    fontFamily: 'Inter, sans-serif',
    color: '#1e293b'
  }),
  headerStyle: {
    backgroundColor: '#0f172a',
    color: '#f8fafc',
    fontWeight: 600,
    fontSize: '0.8125rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    padding: '14px 16px',
    fontFamily: 'Inter, sans-serif',
    borderBottom: '1px solid #1e293b',
    whiteSpace: 'nowrap'
  },
  pageSize: 10,
  pageSizeOptions: [10, 20, 50],
  actionsCellStyle: {
    padding: '8px 16px',
    whiteSpace: 'nowrap'
  },
  ...additionalOptions
});

export const PageHeader = ({ title, subtitle, count, action }) => (
  <Box sx={{
    mb: 3,
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    justifyContent: 'space-between',
    alignItems: { xs: 'flex-start', sm: 'center' },
    gap: 2
  }}>
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em' }}>
          {title}
        </Typography>
        {count !== undefined && count !== null && (
          <Chip
            label={`${count} registros`}
            size="small"
            sx={{
              bgcolor: '#e2e8f0',
              color: '#334155',
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 24,
              borderRadius: '6px'
            }}
          />
        )}
      </Box>
      {subtitle && (
        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontSize: '0.875rem' }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {action && (
      <Box sx={{ display: 'flex', gap: 1 }}>
        {action}
      </Box>
    )}
  </Box>
);

export const MercosulPlateBadge = ({ plate }) => {
  if (!plate) return <Typography variant="caption" sx={{ color: '#94a3b8' }}>-</Typography>;
  const cleanPlate = plate.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return (
    <Box sx={{
      display: 'inline-flex',
      flexDirection: 'column',
      border: '1.5px solid #000',
      borderRadius: '4px',
      overflow: 'hidden',
      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
      minWidth: 84,
      bgcolor: '#fff'
    }}>
      <Box sx={{
        bgcolor: '#003399',
        color: '#fff',
        px: 0.75,
        py: 0.15,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '0.55rem',
        fontWeight: 800,
        letterSpacing: '0.05em'
      }}>
        <span>BRASIL</span>
        <span style={{ fontSize: '0.5rem' }}>BR</span>
      </Box>
      <Box sx={{
        px: 1,
        py: 0.2,
        textAlign: 'center',
        fontWeight: 800,
        fontSize: '0.8125rem',
        letterSpacing: '0.12em',
        color: '#000',
        fontFamily: 'monospace'
      }}>
        {cleanPlate}
      </Box>
    </Box>
  );
};

export const RouteStatusChip = ({ status, t }) => {
  const s = (status || '').toUpperCase();
  let bg = '#f1f5f9';
  let color = '#475569';
  let label = s;

  switch (s) {
    case 'NEW':
    case 'PENDING':
      bg = '#fef3c7';
      color = '#b45309';
      label = t ? t('PENDING') || 'Pendente' : 'Pendente';
      break;
    case 'ACCEPTED':
    case 'STARTED':
      bg = '#dbeafe';
      color = '#1d4ed8';
      label = t ? t('STARTED') || 'Em Andamento' : 'Em Andamento';
      break;
    case 'ARRIVED':
    case 'REACHED':
      bg = '#f3e8ff';
      color = '#7e22ce';
      label = t ? t('REACHED') || 'No Local' : 'No Local';
      break;
    case 'COMPLETE':
    case 'PAID':
      bg = '#dcfce7';
      color = '#15803d';
      label = t ? t('COMPLETE') || 'Concluída' : 'Concluída';
      break;
    case 'CANCELLED':
      bg = '#fee2e2';
      color = '#b91c1c';
      label = t ? t('CANCELLED') || 'Cancelada' : 'Cancelada';
      break;
    default:
      bg = '#f1f5f9';
      color = '#475569';
      label = t ? t(status) || status : status;
  }

  return (
    <Chip
      size="small"
      label={label}
      sx={{
        bgcolor: bg,
        color: color,
        fontWeight: 700,
        fontSize: '0.75rem',
        borderRadius: '6px',
        px: 0.5,
        height: 24
      }}
    />
  );
};
